import * as THREE from "three";

// IRON MARKET city massing. From gunship altitude architecture is silhouette
// and color (WORLD_PRINCIPLES §4): dense flat-roofed blocks with parapets and
// roof clutter, a market street with awnings, an open plaza with a minaret
// landmark, collapsed blocks as rubble. Buildings are real LOS occluders and
// navmesh obstacles; the streets between blocks stay walkable.

type HeightFn = (x: number, z: number) => number;
type RouteDistFn = (x: number, z: number) => number;

function rng32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return (s >>> 0) / 0xffffffff;
  };
}

// City grid: block cells on a square lattice around the origin.
export const CITY_HALF = 470; // lattice extent from center
export const BLOCK = 72; // lattice pitch
export const STREET = 15; // street width between blocks

// Distance to the nearest street centerline (for the asphalt ground paint).
// Streets run along lattice-cell boundaries.
export function distanceToStreetGrid(x: number, z: number): number {
  if (Math.max(Math.abs(x), Math.abs(z)) > CITY_HALF + BLOCK) return Infinity;
  const off = BLOCK / 2;
  const fx = Math.abs((((x - off) % BLOCK) + BLOCK * 1.5) % BLOCK - BLOCK / 2);
  const fz = Math.abs((((z - off) % BLOCK) + BLOCK * 1.5) % BLOCK - BLOCK / 2);
  return Math.min(fx, fz);
}

// Walls sit well below white so the golden key has headroom — pale albedo
// cooks to cream under this sun (the valley taught us). Roofs get their own
// darker concrete tone: from altitude the ROOF is the building.
const WALL_COLORS = [0xa08a64, 0x94805e, 0xa8926e, 0x8a7856, 0x9c8868, 0x877a5e];
const ACCENT_COLORS = [0x5f7d8c, 0x7d5f52, 0x6e7d55];
const ROOF_COLORS = [0x7c7364, 0x746b5c, 0x83796a];
const PARAPET = 0x6e6152;
const RUBBLE = 0x6f6552;
const AWNING_COLORS = [0x9a4f3a, 0x5a7548, 0x96793a, 0x4a6578];

const wallMats = WALL_COLORS.map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.95, metalness: 0, flatShading: true }));
const accentMats = ACCENT_COLORS.map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.95, metalness: 0, flatShading: true }));
const roofMats = ROOF_COLORS.map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 1, metalness: 0, flatShading: true }));
const parapetMat = new THREE.MeshStandardMaterial({ color: PARAPET, roughness: 1, metalness: 0, flatShading: true });
const rubbleMat = new THREE.MeshStandardMaterial({ color: RUBBLE, roughness: 1, metalness: 0, flatShading: true });
const roofClutterMat = new THREE.MeshStandardMaterial({ color: 0x8c8578, roughness: 0.9, metalness: 0.05, flatShading: true });
const awningMats = AWNING_COLORS.map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.9, metalness: 0, flatShading: true }));

function addBuilding(
  parent: THREE.Group,
  rnd: () => number,
  cx: number,
  cz: number,
  w: number,
  d: number,
  floors: number,
  heightAt: HeightFn,
): void {
  const h = floors * 4.2 + rnd() * 1.5;
  const y = heightAt(cx, cz);
  const mat = rnd() < 0.12
    ? accentMats[Math.floor(rnd() * accentMats.length)]!
    : wallMats[Math.floor(rnd() * wallMats.length)]!;

  // Per-face materials: index 2 is +Y — the roof reads as concrete, not wall
  const roof = roofMats[Math.floor(rnd() * roofMats.length)]!;
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    [mat, mat, roof, mat, mat, mat],
  );
  body.position.set(cx, y + h / 2, cz);
  body.castShadow = true;
  body.receiveShadow = true;
  parent.add(body);

  // Parapet lip — the signature flat-roof silhouette from above
  const lip = new THREE.Mesh(new THREE.BoxGeometry(w + 0.9, 0.9, d + 0.9), parapetMat);
  lip.position.set(cx, y + h + 0.25, cz);
  lip.castShadow = true;
  parent.add(lip);

  // Roof clutter: water tanks and stair heads read from altitude
  if (rnd() < 0.6) {
    const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 1.6, 7), roofClutterMat);
    tank.position.set(cx + (rnd() - 0.5) * w * 0.5, y + h + 1.1, cz + (rnd() - 0.5) * d * 0.5);
    tank.castShadow = true;
    parent.add(tank);
  }
  if (rnd() < 0.45) {
    const stair = new THREE.Mesh(new THREE.BoxGeometry(2.4, 2.2, 2.9), mat);
    stair.position.set(cx + (rnd() - 0.5) * w * 0.4, y + h + 1.1, cz + (rnd() - 0.5) * d * 0.4);
    stair.castShadow = true;
    parent.add(stair);
  }
}

function addRubbleBlock(parent: THREE.Group, rnd: () => number, cx: number, cz: number, lot: number, heightAt: HeightFn): void {
  const y = heightAt(cx, cz);
  const piles = 5 + Math.floor(rnd() * 5);
  for (let i = 0; i < piles; i++) {
    const s = 2 + rnd() * 5;
    const pile = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 0), rubbleMat);
    pile.position.set(
      cx + (rnd() - 0.5) * lot * 0.7,
      y + s * 0.25,
      cz + (rnd() - 0.5) * lot * 0.7,
    );
    pile.scale.y = 0.45;
    pile.rotation.y = rnd() * Math.PI;
    pile.castShadow = true;
    pile.receiveShadow = true;
    parent.add(pile);
  }
  // One standing wall fragment — reads as a destroyed building, not a quarry
  const frag = new THREE.Mesh(new THREE.BoxGeometry(lot * 0.4, 6 + rnd() * 5, 1.4), wallMats[0]!);
  frag.position.set(cx + (rnd() - 0.5) * lot * 0.3, y + 3.5, cz + (rnd() - 0.5) * lot * 0.3);
  frag.rotation.y = rnd() * 0.6;
  frag.castShadow = true;
  parent.add(frag);
}

function addMarketStalls(parent: THREE.Group, rnd: () => number, heightAt: HeightFn, routeDist: RouteDistFn): void {
  // Awning stalls hug the market segment of the route (MARKET SOUTH →
  // PLAZA): scatter alongside where routeDist is 10-16 (street edges)
  let placed = 0;
  let attempts = 0;
  while (placed < 42 && attempts < 3000) {
    attempts++;
    const x = -300 + rnd() * 380;
    const z = -260 + rnd() * 320;
    const d = routeDist(x, z);
    if (d < 9 || d > 15) continue;
    const y = heightAt(x, z);
    const stall = new THREE.Group();
    const canopy = new THREE.Mesh(
      new THREE.BoxGeometry(3.4 + rnd() * 1.6, 0.3, 2.6),
      awningMats[Math.floor(rnd() * awningMats.length)]!,
    );
    canopy.position.y = 2.5;
    canopy.rotation.z = (rnd() - 0.5) * 0.12;
    const table = new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.0, 1.8), roofClutterMat);
    table.position.y = 0.5;
    stall.add(canopy, table);
    stall.position.set(x, y, z);
    stall.rotation.y = rnd() * Math.PI * 2;
    stall.traverse((o) => {
      o.castShadow = true;
      o.userData["losTransparent"] = true; // stalls never block sight or nav
      o.userData["ignoreBallistics"] = true;
    });
    parent.add(stall);
    placed++;
  }
}

function addMinaret(parent: THREE.Group, x: number, z: number, heightAt: HeightFn): void {
  const y = heightAt(x, z);
  const g = new THREE.Group();
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 3.4, 52, 8), wallMats[2]!);
  shaft.position.y = 26;
  const balcony = new THREE.Mesh(new THREE.CylinderGeometry(4.4, 4.4, 1.8, 8), parapetMat);
  balcony.position.y = 44;
  const cap = new THREE.Mesh(new THREE.ConeGeometry(3.2, 7, 8), accentMats[0]!);
  cap.position.y = 56;
  g.add(shaft, balcony, cap);
  g.position.set(x, y, z);
  g.traverse((o) => { o.castShadow = true; });
  parent.add(g);
}

// The plaza: an open lattice cell with a monument — breathing room in the
// density, and the mission's centerpiece hold.
function addPlaza(parent: THREE.Group, x: number, z: number, heightAt: HeightFn): void {
  const y = heightAt(x, z);
  const slab = new THREE.Mesh(
    new THREE.CylinderGeometry(3.2, 3.6, 1.2, 6),
    parapetMat,
  );
  slab.position.set(x, y + 0.6, z);
  const obelisk = new THREE.Mesh(new THREE.BoxGeometry(1.6, 9, 1.6), wallMats[3]!);
  obelisk.position.set(x, y + 5.5, z);
  slab.castShadow = true;
  obelisk.castShadow = true;
  parent.add(slab, obelisk);
}

// Rooftop AA perches: visibly sandbagged roof corners at the authored flak
// positions so the threat reads before it fires (mission flakSites point at
// these; the platform itself is dressing).
export const ROOFTOP_AA_PERCHES: Array<[number, number]> = [
  [-56, -132], // over the market street
  [204, 204], // over the civic block, covering the extraction lot
];

function addAAPerch(parent: THREE.Group, x: number, z: number, heightAt: HeightFn): void {
  // A dedicated taller building with a sandbag ring on the roof
  const y = heightAt(x, z);
  const h = 22;
  const body = new THREE.Mesh(new THREE.BoxGeometry(16, h, 16), wallMats[4]!);
  body.position.set(x, y + h / 2, z);
  body.castShadow = true;
  body.receiveShadow = true;
  const ring = new THREE.Mesh(
    new THREE.CylinderGeometry(4.4, 4.7, 1.3, 8, 1, true),
    new THREE.MeshStandardMaterial({ color: 0x8b7a55, roughness: 1, flatShading: true, side: THREE.DoubleSide }),
  );
  ring.position.set(x, y + h + 0.7, z);
  parent.add(body, ring);
}

// Cells the route corridor passes through stay unbuilt (streets widen into
// the corridor) — plus the plaza cell.
const PLAZA_CELL: [number, number] = [0, 0];

export function addIronMarketCity(parent: THREE.Group, heightAt: HeightFn, routeDist: RouteDistFn): void {
  const rnd = rng32(0x19891107);
  const city = new THREE.Group();
  city.name = "iron-market-city";

  const lot = BLOCK - STREET;
  for (let gx = -CITY_HALF; gx <= CITY_HALF; gx += BLOCK) {
    for (let gz = -CITY_HALF; gz <= CITY_HALF; gz += BLOCK) {
      const cellIdx: [number, number] = [Math.round(gx / BLOCK), Math.round(gz / BLOCK)];
      if (cellIdx[0] === PLAZA_CELL[0] && cellIdx[1] === PLAZA_CELL[1]) continue;

      // Corridor cells stay open so the squad has a street to walk
      if (routeDist(gx, gz) < 34) continue;

      // Dense core, thinning only at the rim — this is a CITY
      const centerDist = Math.hypot(gx, gz);
      const density = 1 - THREE.MathUtils.smoothstep(centerDist, 340, CITY_HALF + 80);
      if (rnd() > 0.55 + density * 0.45) continue;

      // Some blocks took a hit
      if (rnd() < 0.12) {
        addRubbleBlock(city, rnd, gx, gz, lot, heightAt);
        continue;
      }

      // 4-6 buildings per block, packed shoulder to shoulder
      const count = 4 + Math.floor(rnd() * 3);
      const tall = centerDist < 260; // core rises
      for (let b = 0; b < count; b++) {
        const w = 16 + rnd() * 20;
        const d = 16 + rnd() * 20;
        const bx = gx + (rnd() - 0.5) * Math.max(2, lot - w) * 0.9;
        const bz = gz + (rnd() - 0.5) * Math.max(2, lot - d) * 0.9;
        const floors = tall ? 2 + Math.floor(rnd() * 3) : 1 + Math.floor(rnd() * 2);
        addBuilding(city, rnd, bx, bz, w, d, floors, heightAt);
      }
    }
  }

  addPlaza(city, 30, 20, heightAt);
  addMinaret(city, 66, -14, heightAt);
  addMarketStalls(city, rnd, heightAt, routeDist);
  for (const [x, z] of ROOFTOP_AA_PERCHES) addAAPerch(city, x, z, heightAt);

  parent.add(city);
}
